import StyledButton from "components/shared/StyledButton";
import TextButton from "components/shared/TextButton";
import type { CSSProperties, Dispatch, ReactNode, SetStateAction } from "react";
import { useTranslation } from "react-i18next";

import { IHandleStep } from "..";
import styles from "../style.module.scss";

interface IStepWrapper {
    children: ReactNode;
    activeStep: number;
    setActiveStep: Dispatch<SetStateAction<number>>;
    /**
     * Condition to disable "Continue" button.
     */
    conditionToDisable?: boolean;
    /**
     * Condition to block "Continue" button.
     * Difference is that they can click, but an action is triggered instead of passing to next step.
     */
    conditionToBlock?: boolean;
    /**
     * Action triggered when "Continue" button is blocked.
     */
    handleTrigger?: () => void;
    /**
     * Message to display next to buttons when block is triggered.
     */
    blockMessage?: string;
    style?: CSSProperties;
    /**
     * Action that needs to be triggered on top of doing next.
     */
    extraAction?: () => void;
    /**
     * Triggers submit action;
     */
    handleSubmit?: () => void;
    /**
     * Custom text for Next button if different than simple Continue.
     */
    customNextBtnText?: string;
    /**
     * Action to clear data when going back a step.
     */
    clearData?: () => void;
    /**
     * Whether to hide previous button.
     */
    hidePrevious?: boolean;
}

/**
 * Wrapper for steps of the import modal. Handles spacing between content and buttons.
 * Also handles buttons.
 */
const StepWrapper = ({
    children,
    activeStep,
    setActiveStep,
    conditionToBlock = false,
    conditionToDisable = false,
    handleTrigger,
    blockMessage,
    style,
    extraAction,
    handleSubmit,
    customNextBtnText,
    clearData,
    hidePrevious = false,
}: IStepWrapper) => {
    const [t] = useTranslation();
    const handleStep = {
        change: (_: any, idx: number) => setActiveStep(idx),
        next: () => {
            if (blockMessage) {
                handleTrigger && handleTrigger();
            } else if (handleSubmit) {
                handleSubmit();
            } else {
                extraAction && extraAction();
                setActiveStep((prev) => prev + 1);
            }
        },
        previous: () => {
            clearData && clearData();
            setActiveStep((prev) => prev - 1);
        },
    };

    return (
        <div className={styles["step-wrapper"]} style={style}>
            {children}
            <div className={styles["action-buttons"]}>
                {conditionToBlock ? <span className={styles["block-message"]}>{blockMessage}</span> : <></>}
                {activeStep > 0 && !hidePrevious ? (
                    <TextButton
                        handleClick={handleStep.previous}
                        text={t("BulkUpdates.Steps.PreviousStep")}
                        style={{ color: "grey", fontSize: "14px", fontWeight: "400", textDecoration: "underline" }}
                    />
                ) : (
                    <div></div>
                )}
                <StyledButton
                    buttonType="primary"
                    onClick={handleStep.next}
                    disabled={conditionToDisable}
                    sx={{ height: "2.5rem !important" }}
                >
                    {customNextBtnText ?? t("BulkUpdates.Steps.Continue")}
                </StyledButton>
            </div>
        </div>
    );
};

export default StepWrapper;
